import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import type { MemberAction } from "./member-actions";

const LABEL_KEYS: Record<MemberAction, string> = {
  changeRole: "organizationAccess.changeRole.action",
  manageLocations: "organizationAccess.manageLocations.action",
  reactivate: "organizationAccess.reactivate.action",
};

const ARIA_LABEL_KEYS: Record<MemberAction, string> = {
  changeRole: "organizationAccess.memberActions.changeRoleFor",
  manageLocations: "organizationAccess.memberActions.manageLocationsFor",
  reactivate: "organizationAccess.memberActions.reactivateFor",
};

const TEST_ID_PREFIXES: Record<MemberAction, string> = {
  changeRole: "member-change-role",
  manageLocations: "member-manage-locations",
  reactivate: "member-reactivate",
};

interface MemberActionsProps {
  readonly membershipId: string;
  /** Names the member in each button's accessible label. */
  readonly memberName: string;
  readonly actions: readonly MemberAction[];
  readonly onAction: (action: MemberAction) => void;
}

/** The buttons of one roster row: only the ones the viewer may use on this member. */
export function MemberActions({ membershipId, memberName, actions, onAction }: MemberActionsProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {actions.map((action) => (
        <Button
          key={action}
          type="button"
          variant="outline"
          size="sm"
          aria-label={t(ARIA_LABEL_KEYS[action], { name: memberName })}
          data-testid={`${TEST_ID_PREFIXES[action]}-${membershipId}`}
          onClick={() => onAction(action)}
        >
          {t(LABEL_KEYS[action])}
        </Button>
      ))}
    </div>
  );
}
